import { CloseRounded } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import SearchIcon from 'common/customIcons/search';
import MDButton from 'components/MDButton';
import { AgGridTable } from 'components/tables/AgGridTable';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CommonLayout from 'layouts/DashboardLayout';
import { Link, useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import { useSelector, useDispatch } from 'react-redux';
import { deleteDistributor, fetchDistributors, syncDistributor } from '../../redux/slices/distributor';
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from '../../redux/slices/modalSlice';
import { openSnackbar } from '../../redux/slices/common';
import FilterDistributors from 'common/CommonDrawer/distributors/FilterDistributors';
import { useDebounce } from 'hooks/useDebounce';
import StatusText from 'common/component/StatusText';
import { withStyles } from '@mui/styles';
import { getData, setData } from 'utilities/common';

const BASE_PATH = '/hyperwolf/distributors';

export default function Distributors() {
  const { distributorsList = {}, isLoading: loading = false, total = 0, syncDistributorLoader = false } = useSelector((state) => state.distributor);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchTermRef = useRef('');
  const [searchTerm, setSearchTerm] = useState('');
  const distributorFiltersPayload = useRef({});
  const sortRef = useRef({ sortBy: 'distributorName', order: 'asc' });
  const mainGridApiRef = useRef('');
  const pageRef = useRef(0);
  const gridRefApi = useRef(null);
  const [waitingTime, setWaitingTime] = useState('');
  const waitingTimeInterval = useRef(null);

  const LightTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
  }))(Tooltip);

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [total, loading]);

  const columnDefs = [
    {
      headerName: 'Distributor Name',
      field: 'distributorName',
      suppressMenu: false,
      minWidth: 160,
      sortingOrder: ['asc', 'desc'],
    },
    {
      headerName: 'License Number',
      field: 'distributorLicenseNumber',
      suppressMenu: false,
      minWidth: 140,
    },
    {
      headerName: 'Contact Name',
      field: 'contactPersonName',
      suppressMenu: false,
      minWidth: 140,
    },
    {
      headerName: 'Distributor Billing Email',
      field: 'distributorBillingEmail',
      suppressMenu: false,
      minWidth: 200,
    },
    {
      headerName: 'Phone Number',
      field: 'phoneNumber',
      suppressMenu: false,
      minWidth: 140,
      valueGetter: (params) => {
        const phone = params.data?.phoneNumber;
        if (!phone) return '-';
        // Always display with +1 prefix
        return `+1 ${phone}`;
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      minWidth: 100,
      cellRenderer: (props) => {
        const status = props?.data?.status;
        if (status === undefined || status === null) return '-';
        const statusLabel = status === true || status === 'active' ? 'active' : 'inactive';
        return <StatusText status={statusLabel} />;
      },
    },
    {
      headerName: 'Action',
      suppressMenu: true,
      cellRenderer: (props) => {
        const id = props?.data?._id || props?.data?.distributorId;
        if (!id) return null;
        return (
          <Box display="inline-flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(props);
              }}
              color="primary"
              size="small"
            >
              <EditIcon width="16px" height="16px" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                dispatch(
                  openCustomModalDialog({
                    modalData: {
                      deleteHandler: () => deleteHandler(id),
                      deleteId: id,
                      deleteType: 'Distributor',
                    },
                    modal: 'DELETE_MODAL',
                  })
                );
              }}
              color="error"
              size="small"
            >
              <DeleteIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      minWidth: 120,
      pinned: 'right',
    },
  ];

  const getRows = useCallback(
    async (params) => {
      gridRefApi.current = params;
      const filterPayload = distributorFiltersPayload.current?.queryPayload || {};
      const sortModel = params?.sortModel?.[0];
      const nextSortBy = sortModel?.colId || sortRef.current.sortBy || 'distributorName';
      const nextOrder = (sortModel?.sort || sortRef.current.order || 'desc').toLowerCase() === 'asc' ? 'asc' : 'desc';
      if (sortRef.current.sortBy !== nextSortBy || sortRef.current.order !== nextOrder) {
        sortRef.current = { sortBy: nextSortBy, order: nextOrder };
        pageRef.current = 0;
      }
      dispatch(
        fetchDistributors({
          skip: pageRef.current,
          searchTerm: searchTermRef.current ?? '',
          limit: 20,
          ...filterPayload,
          sortBy: nextSortBy,
          order: nextOrder,
        })
      )
        .unwrap()
        .then((res) => {
          const list = res?.data ?? [];
          const total = res?.total ?? 0;
          const rows = (Array.isArray(list) ? list : []).map((item) => ({
            ...item,
            id: item._id || item.distributorId,
          }));
          params.successCallback(rows, total);
          pageRef.current = pageRef.current + 20;
          if (total === 0 && mainGridApiRef?.current) {
            mainGridApiRef.current.showNoRowsOverlay();
          }
        })
        .catch(() => {
          params.successCallback([], 0);
          if (mainGridApiRef?.current) {
            mainGridApiRef.current.showNoRowsOverlay();
          }
        });
    },
    [dispatch]
  );

  const searchFn = () => {
    pageRef.current = 0;
    const dataSource = { getRows };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption('datasource', dataSource);
    }
  };

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
  };

  const runWaitingTimeInterval = () => {
    if (getData('adminDistributorLastSync')) {
      const value = JSON.parse(getData('adminDistributorLastSync'));
      waitingTimeInterval.current = setInterval(() => {
        const diffInMin = Math.floor(((Date.now() - value) / 1000 / 60) << 0);
        if (diffInMin > 2) {
          setWaitingTime('');
          clearInterval(waitingTimeInterval.current);
        }
        let waitingMsg = '';
        if (diffInMin <= 2) waitingMsg = 'cooldown';
        setWaitingTime(waitingMsg);
      }, 2000);
    }
  };

  const onSyncBlazeDistributors = () => {
    if (!syncDistributorLoader && !waitingTime) {
      dispatch(syncDistributor())
        .unwrap()
        .then((res) => {
          setData('adminDistributorLastSync', Date.now());
          setWaitingTime('cooldown');
          runWaitingTimeInterval();
          dispatch(
            openSnackbar({
              content: res?.message || 'Distributors synced successfully',
              color: 'success',
              icon: 'check',
            })
          );
          searchFn();
        })
        .catch((err) => {
          dispatch(
            openSnackbar({
              content: err?.message || err?.error || 'Failed to sync distributors',
              color: 'error',
              icon: 'warning',
            })
          );
        });
    }
  };

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName === 'Action') return;
    const id = row?.data?._id || row?.data?.distributorId;
    const blazeVendorId = row?.data?.blazeVendorId;
    if (id) {
      const query = blazeVendorId ? `?blazeVendorId=${encodeURIComponent(blazeVendorId)}` : '';
      navigate(`${BASE_PATH}/${id}${query}`);
    }
  };

  const onClickEdit = (props) => {
    const id = props?.data?._id || props?.data?.distributorId;
    const blazeVendorId = props?.data?.blazeVendorId;
    if (id) {
      const query = blazeVendorId ? `?blazeVendorId=${encodeURIComponent(blazeVendorId)}` : '';
      navigate(`${BASE_PATH}/${id}/edit${query}`);
    }
  };

  const deleteHandler = (distributorId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteDistributor(distributorId))
      .unwrap()
      .then((res) => {
        showError(res?.message, 'success');
        dispatch(closeCustomModalDialog());
        pageRef.current = 0;
        searchFn();
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showError(err?.message || 'Failed to delete', 'error');
      });
  };

  const showError = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const applyDistributorFilter = (filterPayload) => {
    pageRef.current = 0;
    distributorFiltersPayload.current = filterPayload;
    if (!mainGridApiRef?.current) return;
    const dataSource = { getRows };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption('datasource', null);
    requestAnimationFrame(() => {
      if (mainGridApiRef?.current) {
        mainGridApiRef.current.setGridOption('datasource', dataSource);
      }
    });
  };

  useEffect(() => {
    if (mainGridApiRef?.current?.api) {
      if (loading) {
        mainGridApiRef.current.api.showLoadingOverlay();
      } else {
        mainGridApiRef.current.api.hideOverlay();
      }
    }
  }, [loading]);

  useEffect(() => {
    runWaitingTimeInterval();
    return () => {
      if (waitingTimeInterval.current) clearInterval(waitingTimeInterval.current);
    };
  }, []);

  const lastDistributorSyncLocal = getData('adminDistributorLastSync');
  const lastDistributorSyncDate = lastDistributorSyncLocal
    ? new Date(JSON.parse(lastDistributorSyncLocal))
    : '';
  const lastDistributorSyncDisplay = lastDistributorSyncDate
    ? lastDistributorSyncDate.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
    : '';

  return (
    <CommonLayout>
      <Box sx={{ mt: 2 }}>
        <div className="last-sync">
          <div>Last Sync : {lastDistributorSyncDisplay || 'Syncing Now'}</div>
          <LightTooltip
            TransitionComponent={Zoom}
            title={`${
              syncDistributorLoader
                ? 'Syncing is in Progress'
                : waitingTime
                ? 'Please wait for next sync'
                : 'Sync distributors from blaze'
            }`}
          >
            <div
              className={`product-sync-div ${waitingTime ? 'disabled-text' : ''}`}
              onClick={onSyncBlazeDistributors}
            >
              {syncDistributorLoader ? (
                <CircularProgress size={18} />
              ) : (
                'Sync Distributors'
              )}
            </div>
          </LightTooltip>
        </div>
      </Box>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} className="row-pointer" position="relative">
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
          <Grid item xs={12} sm={2}>
            <Typography variant="h4" >Distributors</Typography>
          </Grid>
          <Grid item xs={12} sm={10}>
            <Stack
              direction="row"
              justifyContent={{ xs: 'start', sm: 'end' }}
              flexWrap="wrap"
              alignItems="stretch"
              gap={2}
            >
              <TextField
                className="custom-search-field"
                placeholder="Search"
                label=""
                value={searchTerm}
                onChange={(e) => searchHandler(e.target.value)}
                sx={{ minWidth: '280px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small">
                        <SearchIcon width="18px" height="18px" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchTerm?.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => searchHandler('')} size="small">
                          <CloseRounded />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
              <Link to={`${BASE_PATH}/add`}>
                <MDButton
                  variant="contained"
                  color="primary"
                  style={{ height: '100%', minWidth: '160px' }}
                  startIcon={<AddRoundedIcon fontSize="large" />}
                >
                  Add Distributor
                </MDButton>
              </Link>
            </Stack>
          </Grid>
        </Grid>
        <FilterDistributors applyDistributorFilter={applyDistributorFilter} />
        <AgGridTable
          col={columnDefs}
          mainGridApiRef={mainGridApiRef}
          getRows={getRows}
          pageRef={pageRef}
          isLoading={loading}
          onRowClicked={onRowClicked}
          style={{ height: '56vh' }}
          gridOptions={{ rowHeight: 64 }}
        />
      </Box>
    </CommonLayout>
  );
}
